// Reproduction fidèle du document source : coordonnées exactes du PDF (canevas 960 x 540).
export const RED = "#8d1f35";
export const INK = "#151515";
export const GREY = "#68645f";
export const IVORY = "#f3f0eb";
export const DARK = "#232323";
const SLATE = "#5f7377";

export const LINE = "#d7d0c8";

export const paras = (text) => (text ? text.split("\n\n") : []);

export const hr = (x1, x2, y, t = 1.1, fill = RED) => ({ x: x1, y, w: x2 - x1, h: t, fill });
export const vr = (x, y1, y2, t = 1.1, fill = LINE) => ({ x, y: y1, w: t, h: y2 - y1, fill });
export const dot = (x, y, d, fill) => ({ x, y, w: d, h: d, fill, round: true });

const charWidth = (it) => (it.serif ? 0.64 : it.bold ? 0.56 : 0.54);

const itemHeight = (it) => {
  const text = it.text || "";
  const perLine = it.w ? Math.max(1, Math.floor(it.w / (charWidth(it) * it.size))) : text.length;
  const lines = Math.max(1, Math.ceil(text.length / perLine));
  return lines * it.size * (it.lh || 1.2);
};

// Resserre les écarts verticaux hérités du document tout en conservant les alignements.
export const compact = (page, maxGap = 30) => {
  const items = page.items.filter((it) => it.text);
  const rects = page.rects || [];
  const nodes = [
    ...items.map((it) => ({ ref: it, y: it.y, h: itemHeight(it) })),
    ...rects.map((r) => ({ ref: r, y: r.y, h: r.h, rect: true })),
  ];
  const rows = [...new Set(nodes.map((n) => Math.round(n.y)))].sort((a, b) => a - b);
  const bottomOf = (y) => Math.max(...nodes.filter((n) => n.y <= y + 0.5).map((n) => n.y + n.h));

  const shiftAt = {};
  let shift = 0;
  rows.forEach((y, i) => {
    shiftAt[y] = shift;
    const next = rows[i + 1];
    if (next === undefined) return;
    const gap = next - bottomOf(y);
    if (gap > maxGap) shift += gap - maxGap;
  });

  const shiftBelow = (y) => {
    let applied = 0;
    rows.forEach((r) => { if (r <= y + 0.5) applied = shiftAt[r]; });
    return applied;
  };

  nodes.forEach((n) => {
    const top = shiftAt[Math.round(n.y)];
    if (n.rect && n.h > 40) n.ref.h -= shiftBelow(n.y + n.h) - top;
    n.ref.y -= top;
  });

  const content = Math.max(...nodes.map((n) => n.ref.y + (n.rect ? n.ref.h : n.h)));
  page.height = Math.round(Math.max(content + 46, 200));
  if (page.image && page.image.h >= 540) page.image.h = page.height;
  return page;
};

export function buildPovPages(insight) {
  const b = insight.blocks;

  const eyebrow = (text, color = RED) => ({ x: 44, y: 46, size: 8.5, bold: true, color, upper: true, ls: "0.14em", text });
  const title = (text, w, lh = 1.12) => ({ x: 44, y: 78, w, size: 25, serif: true, bold: true, color: INK, lh, text });
  const lead = (text, y, w, size = 10.7) => ({ x: 44, y, w, size, color: INK, lh: 1.47, text });

  const pages = [];

  // 01 — couverture
  pages.push({
    bg: DARK,
    bar: RED,
    barW: 14,
    image: { src: insight.image, x: 540, y: 0, w: 420, h: 540 },
    rects: [hr(54, 388, 343, 3)],
    items: [
      { x: 54, y: 139, w: 445, size: 36, serif: true, bold: true, color: "#ffffff", lh: 1.08, text: insight.title },
      { x: 54, y: 370, w: 365, size: 18, bold: true, color: "#ffffff", lh: 1.25, text: insight.subtitle },
      { x: 54, y: 440, w: 375, size: 11.5, color: "#d9d2ca", lh: 1.45, text: insight.standfirst },
    ],
  });

  // 02 — introduction
  {
    const k = b[0];
    const [p1, p2] = paras(k.lead);
    pages.push({
      rects: [
        hr(44, 916, 135),
        { x: 486, y: 185, w: 430, h: 277, fill: IVORY },
        { x: 486, y: 185, w: 5, h: 277, fill: RED },
      ],
      items: [
        eyebrow(k.eyebrow),
        title(k.title, 800),
        lead(p1, 187, 398, 11.2),
        lead(p2, 325, 398, 11.2),
        { x: 526, y: 229, w: 340, size: 25, serif: true, bold: true, color: INK, lh: 1.14, text: k.quote },
        { x: 526, y: 338, w: 345, size: 11, color: GREY, lh: 1.45, text: k.after },
      ],
    });
  }

  // 03 — synthèse
  {
    const k = b[1];
    const items = [eyebrow(k.eyebrow), title(k.title, 620)];
    k.items.forEach((it, i) => {
      const y = 191 + 79 * i;
      const n = String(i + 1).padStart(2, "0");
      items.push({ x: 44, y: y + 1, size: 10, bold: true, color: RED, text: n, desktopOnly: true });
      items.push({ x: 102, y, w: 325, size: 14.5, bold: true, color: INK, lh: 1.17, text: it.label, mobileNum: n });
      items.push({ x: 460, y: y - 2, w: 435, size: 10, color: GREY, lh: 1.4, text: it.text });
    });
    const rects = [hr(44, 916, 135), ...[233, 312, 391].map((y) => hr(102, 890, y, 0.7, LINE))];
    pages.push({ rects, items });
  }

  // 04 — repères
  {
    const k = b[2];
    const items = [
      eyebrow(k.eyebrow),
      title(k.title, 700),
      { x: 44, y: 142, w: 800, size: 10.5, color: GREY, lh: 1.4, text: k.lead },
    ];
    [44, 336, 628].forEach((x, i) => {
      items.push({ x, y: 207, w: 245, size: 15, bold: true, color: i === 0 ? RED : INK, lh: 1.15, text: k.items[i].label });
      items.push({ x, y: 286, w: 248, size: 10, color: GREY, lh: 1.45, text: k.items[i].text });
    });
    items.push({ x: 44, y: 416, w: 655, size: 19, serif: true, bold: true, color: INK, lh: 1.2, text: k.after });
    items.push({ x: 44, y: 495, w: 520, size: 6.6, color: GREY, lh: 1.4, text: k.footnote });
    pages.push({ rects: [hr(44, 916, 158), vr(307, 203, 362), vr(599, 203, 362), hr(44, 916, 383, 0.8, LINE)], items });
  }

  // 05 / 09 / 13 — ouvertures de partie
  const part = (k) => ({
    bg: "#0a0a0a",
    bar: RED,
    barW: 15,
    image: { src: k.image, x: 0, y: 0, w: 960, h: 540, overlay: true },
    rects: [hr(55, 390, 294, 0.8, "#ffffff")],
    items: [
      { x: 55, y: 130, w: 640, size: 36, serif: true, bold: true, color: "#ffffff", lh: 1.08, text: k.title },
      { x: 55, y: 321, w: 530, size: 13, color: "#ffffff", lh: 1.22, text: k.lead },
    ],
  });

  pages.push(part(b[3]));

  // 06 — nature du changement
  {
    const k = b[4];
    const [p1, p2] = paras(k.lead);
    pages.push({
      items: [
        eyebrow(k.eyebrow),
        title(k.title, 800),
        lead(p1, 170, 400),
        lead(p2, 313, 400),
        { x: 525, y: 189, w: 330, size: 16, serif: true, bold: true, color: INK, lh: 1.2, text: k.items[0].label },
        { x: 525, y: 232, w: 300, size: 10.5, color: GREY, lh: 1.4, text: k.items[0].text },
        { x: 525, y: 334, w: 345, size: 16, serif: true, bold: true, color: RED, lh: 1.2, text: k.items[1].label },
        { x: 525, y: 378, w: 345, size: 10.5, color: GREY, lh: 1.4, text: k.items[1].text },
        { x: 44, y: 495, w: 520, size: 6.6, color: GREY, lh: 1.4, text: k.footnote },
      ],
      rects: [hr(44, 916, 135), hr(525, 875, 304, 0.8, LINE)],
    });
  }

  // 07 — formes de contribution
  {
    const k = b[5];
    const items = [
      eyebrow(k.eyebrow),
      title(k.title, 780),
      { x: 44, y: 142, w: 810, size: 10.5, color: GREY, lh: 1.4, text: k.lead },
    ];
    k.items.forEach((it, i) => {
      const col = i < 3 ? 0 : 1;
      const y = [199, 291, 383][i % 3];
      items.push({ x: col === 0 ? 44 : 498, y, w: 180, size: 12, bold: true, color: col === 0 ? RED : INK, lh: 1.2, text: it.label });
      items.push({ x: col === 0 ? 234 : 688, y: y - 2, w: 220, size: 9.5, color: GREY, lh: 1.42, text: it.text });
    });
    const rects = [hr(44, 916, 158), hr(44, 454, 256, 0.6, LINE), hr(44, 454, 348, 0.6, LINE), hr(498, 908, 256, 0.6, LINE), hr(498, 908, 348, 0.6, LINE)];
    pages.push({ rects, items });
  }

  // 08 — matrice
  {
    const k = b[6];
    const cells = [
      { x: 500, y: 210, color: INK },
      { x: 700, y: 210, color: RED },
      { x: 500, y: 340, color: INK },
      { x: 700, y: 340, color: RED },
    ];
    const items = [
      eyebrow(k.eyebrow),
      title(k.title, 770),
      { x: 44, y: 171, w: 375, size: 11, color: INK, lh: 1.45, text: k.lead },
      { x: 44, y: 338, w: 300, size: 11, bold: true, color: RED, text: k.aside.label },
      { x: 44, y: 368, w: 355, size: 17, serif: true, bold: true, color: INK, lh: 1.2, text: k.aside.text },
    ];
    k.items.forEach((it, i) => {
      items.push({ x: cells[i].x, y: cells[i].y, w: 185, size: 10.5, bold: true, color: cells[i].color, lh: 1.2, text: it.label });
      items.push({ x: cells[i].x, y: cells[i].y + 41, w: 185, size: 9, color: GREY, lh: 1.4, text: it.text });
    });
    items.push({ x: 630, y: 452, size: 7.5, bold: true, color: GREY, text: k.axis });
    pages.push({
      rects: [
        hr(44, 916, 135),
        { x: 480, y: 177, w: 400, h: 260, fill: IVORY },
        vr(680, 177, 437, 2, "#ffffff"),
        hr(480, 880, 307, 2, "#ffffff"),
      ],
      items,
    });
  }

  pages.push(part(b[7]));

  // 10 — création de valeur
  {
    const k = b[8];
    const [p1, p2] = paras(k.lead);
    const rects = [hr(44, 916, 135)];
    const items = [eyebrow(k.eyebrow), title(k.title, 800), lead(p1, 171, 400), lead(p2, 318, 400)];
    k.items.forEach((it, i) => {
      const y = 184 + 72 * i;
      const n = String(i + 1).padStart(2, "0");
      rects.push(dot(520, y, 26, i === 2 ? RED : DARK));
      if (i < 3) rects.push(vr(533, y + 28, y + 66, 1.3, LINE));
      items.push({ x: 528, y: y + 10, size: 7, bold: true, color: "#ffffff", text: n, desktopOnly: true });
      items.push({ x: 565, y: y + 3, w: 150, size: 11.5, bold: true, color: INK, lh: 1.2, text: it.label, mobileNum: n });
      items.push({ x: 718, y: y + 2, w: 205, size: 9.3, color: GREY, lh: 1.4, text: it.text });
    });
    items.push({ x: 44, y: 495, w: 380, size: 6.6, color: GREY, lh: 1.4, text: k.footnote });
    pages.push({ rects, items });
  }

  // 11 — la valeur humaine change de place
  {
    const k = b[9];
    const idx = k.quote.indexOf(". ");
    const strong = idx > 0 ? k.quote.slice(0, idx + 1) : k.quote;
    const rest = idx > 0 ? k.quote.slice(idx + 2) : "";
    const items = [
      eyebrow(k.eyebrow),
      title(k.title, 540),
      { x: 44, y: 172, w: 425, size: 11.2, color: INK, lh: 1.47, text: k.lead },
      { x: 68, y: 341, w: 345, size: 14, bold: true, color: INK, lh: 1.2, text: strong },
      { x: 68, y: 384, w: 350, size: 10.2, color: GREY, lh: 1.4, text: rest },
      { x: 540, y: 160, w: 170, size: 7.5, bold: true, color: GREY, upper: true, ls: "0.1em", lh: 1.25, text: k.columns[0].label },
      { x: 735, y: 160, w: 135, size: 7.5, bold: true, color: RED, upper: true, ls: "0.1em", lh: 1.25, text: k.columns[1].label },
    ];
    k.columns[0].items.forEach((t, i) => {
      items.push({ x: 540, y: 197 + 62 * i, size: 13, color: GREY, text: t });
      items.push({ x: 735, y: 197 + 62 * i, size: 13, bold: true, color: INK, text: k.columns[1].items[i] });
    });
    pages.push({
      rects: [hr(44, 916, 135), { x: 44, y: 316, w: 420, h: 105, fill: IVORY }, ...[207, 269, 331, 393].map((y) => hr(660, 708, y, 1.4))],
      items,
    });
  }

  // 12 — apprentissage
  {
    const k = b[10];
    const [p1, p2] = paras(k.lead);
    const boxes = [
      { x: 520, y: 151, w: 320, fill: RED, tx: 538, ty: 169 },
      { x: 545, y: 226, w: 270, fill: DARK, tx: 563, ty: 244 },
      { x: 570, y: 301, w: 220, fill: SLATE, tx: 588, ty: 319 },
    ];
    const items = [eyebrow(k.eyebrow), title(k.title, 780), lead(p1, 172, 400), lead(p2, 321, 400)];
    k.items.forEach((it, i) => {
      items.push({ x: boxes[i].tx, y: boxes[i].ty, w: 240, size: 11, bold: true, color: "#ffffff", text: it.label });
      items.push({ x: boxes[i].tx, y: boxes[i].ty + 18, w: 240, size: 8.5, color: "#ffffff", text: it.text });
    });
    items.push({ x: 520, y: 398, w: 335, size: 13, serif: true, bold: true, color: INK, lh: 1.22, text: k.after });
    pages.push({
      rects: [hr(44, 916, 135), ...boxes.map((bx) => ({ x: bx.x, y: bx.y, w: bx.w, h: 54, fill: bx.fill }))],
      items,
    });
  }

  pages.push(part(b[11]));

  // 14 — réduction des effectifs
  {
    const k = b[12];
    const [p1, p2] = paras(k.lead);
    const items = [
      eyebrow(k.eyebrow),
      title(k.title, 830),
      lead(p1, 172, 425),
      lead(p2, 345, 425),
      { x: 535, y: 179, w: 320, size: 17, serif: true, bold: true, color: INK, lh: 1.2, text: k.itemsTitle },
    ];
    k.items.forEach((it, i) => {
      const y = 227 + 64 * i;
      const n = String(i + 1).padStart(2, "0");
      items.push({ x: 535, y: y + 2, size: 8, bold: true, color: RED, text: n, desktopOnly: true });
      items.push({ x: 580, y: y + 1, w: 155, size: 11, bold: true, color: INK, lh: 1.2, text: it.label, mobileNum: n });
      items.push({ x: 735, y, w: 150, size: 8.8, color: GREY, lh: 1.35, text: it.text });
    });
    pages.push({ rects: [hr(44, 916, 135), ...[259, 323, 387].map((y) => hr(580, 880, y, 0.6, LINE))], items });
  }

  // 15 — agenda de direction
  {
    const k = b[13];
    const [p1, p2, p3] = paras(k.lead);
    const items = [
      eyebrow(k.eyebrow),
      title(k.title, 660),
      lead(p1, 172, 400, 10.8),
      lead(p2, 290, 400, 10.8),
      lead(p3, 422, 400, 10.8),
      { x: 535, y: 179, w: 300, size: 17, serif: true, bold: true, color: INK, lh: 1.2, text: k.itemsTitle },
    ];
    k.items.forEach((it, i) => {
      const y = 225 + 51 * i;
      const n = String(i + 1).padStart(2, "0");
      items.push({ x: 535, y: y + 1, size: 8, bold: true, color: RED, text: n, desktopOnly: true });
      items.push({ x: 580, y, w: 340, size: 11.2, bold: true, color: INK, lh: 1.2, text: it.label, mobileNum: n });
    });
    pages.push({ rects: [hr(44, 916, 135), ...[246, 297, 348, 399].map((y) => hr(580, 875, y, 0.6, LINE))], items });
  }

  // 16 — conclusion
  {
    const k = b[14];
    pages.push({
      bg: DARK,
      bar: RED,
      barW: 15,
      rects: [hr(54, 388, 375, 3)],
      items: [
        { x: 54, y: 76, size: 9, bold: true, color: "#d6cec6", upper: true, ls: "0.16em", text: k.eyebrow },
        { x: 54, y: 128, w: 745, size: 33, serif: true, bold: true, color: "#ffffff", lh: 1.1, text: k.title },
        { x: 54, y: 271, w: 665, size: 20, bold: true, color: "#e8e0d8", lh: 1.2, text: k.lead },
        { x: 54, y: 404, w: 690, size: 11.5, color: "#cfc7bf", lh: 1.45, text: k.after },
      ],
    });
  }

  return pages.map((p) => compact(p));
}
